import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { track } from "../../lib/analytics";
import { mxn } from "../../lib/reservations";
import { GoalDialog } from "../GoalDialog";
import { Modal } from "../Modal";
import type { ApiEnvelope, TripPage, Wallet } from "../../types";

/**
 * Flow B — the Wallet Libre goal route (B3). Opens Wallet Libre's "Tu sueño" form (title, target,
 * date and photo), prefilled with the trip's name and a suggested target, or with the current goal
 * when the wallet already saves for this trip (FR15). Saving moves no money, restricts nothing and
 * creates neither a request nor an Apartado (FR14).
 */
export function TripGoalDialog({ trip, wallet, open, onOpenChange, suggestedTarget }: {
  trip: TripPage;
  wallet: Wallet | undefined;
  open: boolean;
  onOpenChange(open: boolean): void;
  suggestedTarget: number | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const existing = wallet?.goal && wallet.goal.title && wallet.goal.title.trim().toLowerCase() === trip.name.trim().toLowerCase() ? wallet.goal : null;
  if (!wallet) {
    const params = new URLSearchParams({ titulo: trip.name, viaje: trip.slug });
    if (suggestedTarget) params.set("meta", String(Math.round(suggestedTarget)));
    if (trip.nextDeparture) params.set("fecha", trip.nextDeparture);
    return (
      <Modal open={open} onOpenChange={onOpenChange} label="Crear meta en Wallet Libre">
        <div className="dialog-content">
          <button type="button" className="dialog-close" onClick={() => onOpenChange(false)} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
          <span className="page-eyebrow">Ahorrar para después</span>
          <h2>Empieza con tu Wallet Libre</h2>
          <p>Aún no tienes una Wallet Libre. La abrimos en dos pasos con la meta de este viaje ya capturada: sin monto mínimo y con tu dinero siempre disponible.</p>
          <div className="dialog-form">
            <button type="button" className="primary-button solid full" onClick={() => { onOpenChange(false); navigate(`/wallet/libre/nueva?${params.toString()}`); }}>Abrir mi Wallet Libre</button>
          </div>
        </div>
      </Modal>
    );
  }

  // The route lands on the same "Tu sueño" form as Wallet Libre's "Editar mi sueño" (client review,
  // 13 Oct 2026), prefilled with this trip, so the customer can also change the wallet's photo.
  const prefill = existing
    ? { title: existing.title ?? trip.name, amount: existing.amount ? String(existing.amount) : "", date: existing.date ?? "" }
    : { title: trip.name, amount: suggestedTarget ? String(Math.round(suggestedTarget)) : "", date: trip.nextDeparture ?? "" };

  const saved = (updated: Wallet) => {
    queryClient.setQueryData(["wallets"], (current: ApiEnvelope<{ wallets: Wallet[] }> | undefined) => current
      ? { data: { wallets: current.data.wallets.map((item) => (item.id === updated.id ? updated : item)) } }
      : current);
    track("wallet_goal_saved", { goal_id: updated.id, trip_id: trip.id, mode: existing ? "update" : "create" });
    onOpenChange(false);
    navigate("/wallet/libre", { state: { notice: existing ? "Tu meta se actualizó." : "Tu meta quedó guardada en Wallet Libre.", tripSlug: trip.slug } });
  };

  return (
    <GoalDialog
      wallet={wallet}
      open={open}
      onOpenChange={onOpenChange}
      prefill={prefill}
      onSaved={saved}
      hint={suggestedTarget ? <>Sugerido a partir del estimado actual del viaje: <span translate="no">{mxn(suggestedTarget)}</span>. Puedes cambiarlo.</> : null}
      footnote="Guardar la meta no transfiere dinero ni crea un Apartado. Cuando estés listo, vuelve a este viaje y solicita la reservación."
    />
  );
}
